import { DOCUMENT } from '@angular/common';
import {
  afterNextRender, Component, DestroyRef, EnvironmentInjector, inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';

import { FooterComponent } from './component/layout/footer.component';
import { HeaderComponent } from './component/layout/header.component';
import { AnalyticsService } from './service/analytics.service';
import { PAGE_UNAVAILABLE, PageFailure } from './service/page-failure';
import { ThemeService } from './service/theme.service';
import { AppService } from './service/app.service';
import { WebsiteService } from './service/website.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, HeaderComponent, FooterComponent],
  styles: `
    /* A child of app-root, which styles.css otherwise flattens to display: contents */
    .page-failure {
      display: block;
      position: fixed;
      left: 16px;
      /* Clear of the back-to-top button, 40px wide and 40px in from the right (15px on a phone) */
      right: 96px;
      bottom: 16px;
      z-index: 1100;
      padding: 12px 16px;
      background: #fff;
      border: 1px solid #e65540;
      text-align: center;
    }

    @media (max-width: 576px) {
      .page-failure {
        right: 71px;
      }
    }
    `,
  template: `
    @if (website()) {
      <page-header />
      <router-outlet />
      <page-footer />
    }
    <!-- Fixed, so a visitor scrolled down a long list still sees why the click did nothing -->
    @if (failure(); as failure) {
      <p class="page-failure s-text5 text-danger" role="alert">
        {{failure}}
        <!-- A failed first load has no header or links left to try again with -->
        @if (!website() && !missing()) {
          <button type="button" class="s-text5 text-danger" (click)="reload()"><u>Оновити</u></button>
        }
      </p>
    }
    `
})
export class AppComponent {

  private injector = inject(EnvironmentInjector);
  private destroyRef = inject(DestroyRef);
  private theme = inject(ThemeService);
  private analytics = inject(AnalyticsService);
  private appService = inject(AppService);
  private document = inject(DOCUMENT);

  readonly website = inject(WebsiteService).website;
  readonly failure = inject(PageFailure).message;
  readonly missing = inject(PageFailure).missing;

  constructor() {
    const navigations = inject(Router).events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef));
    navigations
      .pipe(filter(end => end.urlAfterRedirects !== PAGE_UNAVAILABLE))
      .subscribe(() => this.failure.set(undefined));
    // afterNextRender never runs on the server, so the jQuery theme bundle stays browser-only.
    afterNextRender(() => {
      this.appService.start();
      this.refresh();
      navigations.subscribe(() => afterNextRender(() => this.refresh(), { injector: this.injector }));
    });
  }

  reload(): void {
    this.document.defaultView?.location.reload();
  }

  private refresh(): void {
    this.analytics.load();
    this.theme.refresh().catch(error => console.error('Cannot load the theme bundle', error));
  }

}
